/**
 * Reverse geocode lat/lng to a short place label (Nominatim).
 * Results are cached; requests are queued to respect Nominatim rate limits.
 */

const cache = new Map();
let queue = Promise.resolve();

const cacheKey = (lat, lng) =>
  `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;

export const formatPlaceLabelFromCustomer = (rep) => {
  if (rep?.customer_name) {
    const site = rep.customer_location || rep.customer_address;
    return site ? `${rep.customer_name} — ${site}` : rep.customer_name;
  }
  return null;
};

const shortenNominatimAddress = (data) => {
  const a = data?.address || {};
  const locality =
    a.suburb || a.neighbourhood || a.village || a.town || a.city || a.county || a.state_district;
  const region = a.state || a.country;
  const road = a.road || a.residential || a.pedestrian;

  if (road && locality) return `${road}, ${locality}`;
  if (locality && region && locality !== region) return `${locality}, ${region}`;
  if (locality) return locality;
  if (region) return region;

  if (data?.display_name) {
    const parts = String(data.display_name)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.slice(0, 2).join(', ') || parts[0] || null;
  }
  return null;
};

export const reverseGeocodePlace = (lat, lng) => {
  if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) {
    return Promise.resolve(null);
  }

  const key = cacheKey(lat, lng);
  if (cache.has(key)) return Promise.resolve(cache.get(key));

  const task = queue.then(async () => {
    await new Promise((resolve) => setTimeout(resolve, 1100));
    try {
      const url = new URL('https://nominatim.openstreetmap.org/reverse');
      url.searchParams.set('lat', String(lat));
      url.searchParams.set('lon', String(lng));
      url.searchParams.set('format', 'json');

      const res = await fetch(url.toString(), {
        headers: {
          Accept: 'application/json',
          'Accept-Language': 'en',
          'User-Agent': 'QuarryForce-Admin-Dashboard/1.0',
        },
      });
      if (!res.ok) throw new Error('reverse geocode failed');
      const data = await res.json();
      const label = shortenNominatimAddress(data);
      cache.set(key, label);
      return label;
    } catch {
      cache.set(key, null);
      return null;
    }
  });

  queue = task.catch(() => {});
  return task;
};

export const resolvePlaceLabelForRep = async (rep) => {
  const fromCustomer = formatPlaceLabelFromCustomer(rep);
  if (fromCustomer) return fromCustomer;
  if (rep.lat == null || rep.lng == null) return null;
  const place = await reverseGeocodePlace(rep.lat, rep.lng);
  return place || 'Unknown area';
};
